import {useAppDispatch, useAppSelector, useModal} from '@hooks';
import {trackEvent} from '@lib/infrastructure';
import {
  clearDesks,
  getIsLastNodeSelected,
  getNodeTypeAndId,
  getSelectedNodes,
  getUser,
  MeetingInviteeSearchResult,
  resetSuggestedDesk,
  resetWorkspaceReservation,
  ValidationError,
} from '@lib/store';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {OfficeWorkday, WorkspaceReservation} from '@lib/store/src/data/timeslots/apiTypes';
import {dateKeyForDateTimeString} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {ModalCell} from '@molecules';
import {
  AddWorkspaceInviteesCard,
  BookWorkspaceValidationError,
  ModalPageBottom,
  SelectInviteesButton,
  SelectWorkspaceButton,
  WorkspaceOnMapSection,
} from '@organisms';
import {Div, FlexCol, H4} from '@quarks';
import {isPlainObject} from '@reduxjs/toolkit';
import {generateTestIdCoreString} from '@utils';
import {useEffect, useMemo, useState} from 'react';
import toast from 'react-hot-toast';
import {useTranslation} from 'react-i18next';
import {useTheme} from 'styled-components';
import {reservationStateEqual, reservationStateFromExisting, reservationStateFromUI} from './ReservationState';
import {StyledFlexCol, StyledModalPage} from './styles';
import {ErrorsData} from './types';
import {Attendee} from '@lib/store/src/data/timeslots/selectors/getAttendeeOverview';
import {useWorkspaceHasSpaceForGroupBooking} from '@lib/store/src/ui/hooks/timeslots/useWorkspaceHasSpaceForGroupBooking';
import {getBuildingIdsThatRequireWorkspaceBooking} from '@lib/store/src/data/timeslots/selectors/getBuildingSections';
import {Loader} from '@atoms';

type BookWorkspaceCardProps = {
  workday: OfficeWorkday;
  workspaceReservation?: WorkspaceReservation;
  members?: Array<Attendee>;
};

// This wrapper ensures we have a sensible last selection
export const BookWorkspaceCard = (props: BookWorkspaceCardProps) => {
  const dispatch = useAppDispatch();
  const buildingId = props.workday.buildingId;

  const lastBookedLocationEntry = useAppSelector((state) => {
    const entry = state.data.timeslots.lastBookedWorkspacesByBuildingId[buildingId];
    return entry;
  });

  useEffect(
    function loadLastBookedWorkspaceIfRequired() {
      // Not needed if we have a reservation already
      if (props.workspaceReservation) return;

      // Never fetch it if we have something. The only reason for it being outdated would be if in another session
      // the user has been making other reservations.
      if (lastBookedLocationEntry) return;

      // Without a building id there's nothing to fetch
      // Note: it would be a bit worrying if we ever don't have a building id...
      if (!buildingId) return;

      dispatch(actions.getLastBookedWorkspaceForBuilding(buildingId));
    },
    [lastBookedLocationEntry, buildingId, dispatch, props.workspaceReservation],
  );

  if (!lastBookedLocationEntry || lastBookedLocationEntry.status === 'pending') {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader />
      </div>
    );
  }

  return <_BookWorkspaceCard {...props} />;
};

const _BookWorkspaceCard = ({workday, workspaceReservation, members = []}: BookWorkspaceCardProps) => {
  const date = dateKeyForDateTimeString(workday.localStart);
  const TEST_ID_CORE = generateTestIdCoreString(import.meta.url);

  const dispatch = useAppDispatch();
  const theme = useTheme();
  const {t} = useTranslation();

  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const {closeModal, addPages, removePages, modalPages} = useModal();

  const {id: userId} = useAppSelector(getUser);

  //// LOCATION
  const {buildingId, floorId, areaId, deskIds} = useAppSelector(getSelectedNodes);
  const buildingIdsWithRequiredWorkspaceBooking = useAppSelector(getBuildingIdsThatRequireWorkspaceBooking);
  const workdayRequiresWorkspaceBooking = buildingIdsWithRequiredWorkspaceBooking.has(workday.buildingId);

  //// PEOPLE
  // TODO: why do we not use the workspaceReservation ui store for this? There is an inviteesSelected...
  const initialInvitees = members.filter((m) => !m.isCurrentUser).map((m) => m.owner);
  const [invitees, setInvitees] = useState<MeetingInviteeSearchResult[]>(initialInvitees);

  const {nodeIds, nodeType} = useMemo(() => {
    return getNodeTypeAndId(buildingId, floorId, areaId, deskIds);
  }, [areaId, buildingId, deskIds, floorId]);

  // Check validity of selections for saving
  const savedReservation = reservationStateFromExisting(members);
  const uiReservation = reservationStateFromUI(
    userId,
    invitees.map((inv) => inv.id),
    nodeIds,
  );
  const hasChanges = !reservationStateEqual(savedReservation, uiReservation);

  // TODO: do various checks whether location is bookable etc.
  //      -> replace getCanSaveWorkspaceReservation

  const hasSpaceForGroupBooking = useWorkspaceHasSpaceForGroupBooking({
    workday,
    buildingId,
    floorId,
    areaId,
    inviteeCount: invitees.length + 1,
  });

  const locationIsValid = hasSpaceForGroupBooking;
  const hasBookableSelection = useAppSelector(getIsLastNodeSelected);
  const canSubmit = hasBookableSelection && hasChanges && locationIsValid;

  // TODO: move to setInvitees
  useEffect(
    function clearErrorMessages() {
      setErrorMessages([]);
    },
    [invitees, nodeIds],
  );

  useEffect(() => {
    return () => {
      dispatch(resetWorkspaceReservation());
      dispatch(clearDesks());
      dispatch(resetSuggestedDesk());
    };
  }, [dispatch]);

  const handleOnSuccess = () => {
    const isGroupBooking = !!invitees.length;
    const isEdit = workspaceReservation !== undefined;
    const getFeedbackMessage = () => {
      if (isGroupBooking && isEdit) {
        return t('workplace:WorkplaceUpdatedMessage');
      }
      if (isGroupBooking) {
        return t('workplace:GroupBookingSent');
      }
      if (nodeType === 'Desk') {
        return t('workplace:DeskBookedMessage');
      }
      if (isEdit) {
        return t('workplace:WorkplaceUpdatedMessage');
      }
      return t('workplace:WorkplaceBookedMessage');
    };
    toast.success(getFeedbackMessage());
    closeModal();

    trackEvent(
      isEdit ? 'CalendarOverview_WorkplaceBooking__UpdateBooking' : 'CalendarOverview_WorkplaceBooking__CreateBooking',
      {
        date,
        isGroupBooking: isEdit ? members.length > 0 : isGroupBooking,
      },
    );
  };

  const saveReservation = async () => {
    try {
      const [nodeIdForUser, ...forInvitees] = nodeIds;
      const base = {
        localStart: workday.localStart,
        localEnd: workday.localEnd,
        nodeId: nodeIdForUser,
        invitations: invitees.map((inv, idx) => {
          // Assign a required `nodeId` to each invitee:
          // - Be sure to use unique desk IDs for desk bookings
          // - Otherwise, we can use the user's nodeId (e.g. the area)
          const nodeId = nodeType === 'Desk' ? forInvitees[idx] : nodeIdForUser;

          return {
            nodeId,
            ownerId: inv.id,
          };
        }),
      };

      // // Note the unwrap; it is required for the error to be thrown
      if (!workspaceReservation) {
        await dispatch(
          actions.createWorkspaceReservation({
            ...base,
            workdayId: workday.id,
          }),
        ).unwrap();
      } else {
        await dispatch(
          actions.saveWorkspaceReservation({
            workspaceReservation,
            patch: base,
          }),
        ).unwrap();
      }

      handleOnSuccess();
    } catch (error: unknown) {
      if (error instanceof ValidationError) {
        const errorValidationMessages = extractErrorMessages(error.data.errors);
        setErrorMessages(errorValidationMessages);
      } else if (error instanceof Error) {
        setErrorMessages([error.message]);
      } else if (isPlainObject(error) && 'message' in error && typeof error.message === 'string') {
        setErrorMessages([error.message]);
      } else {
        setErrorMessages([t('workplace:SomethingWentWrong')]);
        console.error(error);
      }
    }
  };

  const cleanUpWorkdayIfRequired = () => {
    // This is a bandaid that postpones our customers having to reconcile
    // with the fact that having both timeslot booking _and_ mandatory workspace
    // booking does not make much sense.
    if (!workspaceReservation && workdayRequiresWorkspaceBooking) {
      dispatch(actions.cancelWorkday(workday));
    }
  };

  // Modal controls
  const onBack =
    modalPages.length > 1
      ? () => {
          cleanUpWorkdayIfRequired();
          removePages(1);
        }
      : undefined;

  const onClose = () => {
    cleanUpWorkdayIfRequired();
    closeModal();
  };

  return (
    <StyledModalPage
      title={t('workplace:BookAWorkspace')}
      bottom={
        <ModalPageBottom
          buttonDisabled={!canSubmit}
          buttonLabel={workspaceReservation ? t('workplace:Update') : t('translation:Book')}
          onClick={saveReservation}
        />
      }
      onBack={onBack}
      onClose={onClose}>
      <StyledFlexCol gap={8}>
        <Div>
          <FlexCol>
            {errorMessages.map((error, index) => (
              <Div
                key={`error-${index}`}
                background={theme.card.white.background}
                paddingLeft={16}
                paddingTop={16}
                paddingRight={16}>
                <BookWorkspaceValidationError text={error} />
              </Div>
            ))}
          </FlexCol>
          <FlexCol>
            <ModalCell>
              <H4
                as="h2"
                data-testid={`${TEST_ID_CORE}-modal-h2-invitees`}>
                {t('Invitees')}
              </H4>
            </ModalCell>
            <SelectInviteesButton
              placeholder={t('workplace:InviteesPlaceholder')}
              invitees={invitees}
              date={date}
              onClick={() =>
                addPages([
                  <AddWorkspaceInviteesCard
                    passedInvitees={invitees}
                    onSave={setInvitees}
                    onClose={closeModal}
                  />,
                ])
              }></SelectInviteesButton>
          </FlexCol>
        </Div>
        <FlexCol>
          <ModalCell>
            <H4
              as="h2"
              data-testid={`${TEST_ID_CORE}-modal-h2-Desk`}>
              {t('workplace:Workspace')}
            </H4>
          </ModalCell>

          <WorkspaceOnMapSection
            invitees={invitees}
            date={date}
            workday={workday}
            workspaceReservation={workspaceReservation}
          />

          <SelectWorkspaceButton
            workday={workday}
            workspaceReservation={workspaceReservation}
            attendees={members}
            inviteeCount={invitees.length + 1}
          />
        </FlexCol>
      </StyledFlexCol>
    </StyledModalPage>
  );
};

function extractErrorMessages(errors: ErrorsData): string[] {
  return Object.values(errors).flatMap((messages) => messages);
}
